import React from 'react';
import { _T, C } from '../../../shared/theme';
import Avatar from '../../../components/ui/Avatar';

/**
 * Shared turn/score banner for real-friend turn-based games.
 * Props:
 *   myName        {string}
 *   partnerName   {string}
 *   partner       {object}  — for avatar
 *   myTurn        {boolean}
 *   myScore       {number|string}
 *   partnerScore  {number|string}
 *   scoreLabel    {string}  — e.g. "moves" | "ms" | "pts"
 *   statusMsg     {string}  — optional extra message
 *   lang          {string}
 */
function TurnBanner({ myName, partnerName, partner, myTurn, myScore, partnerScore, scoreLabel = '', statusMsg, lang }) {
  const nl = lang === 'nl';
  return (
    <div style={{
      background: myTurn ? `${_T.accent}15` : `rgba(20,32,55,0.85)`,
      border: `1.5px solid ${myTurn ? _T.accent : _T.cardBorderBot}`,
      borderRadius: 16,
      padding: '12px 14px',
      marginBottom: 12,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}>
      {/* Turn indicator */}
      <div style={{
        width: 40, height: 40, borderRadius: '50%',
        background: myTurn ? `${_T.accent}25` : 'rgba(30,50,90,0.6)',
        border: `2px solid ${myTurn ? _T.accent : _T.cardBorderBot}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0,
        animation: myTurn ? 'turnPulse 1.5s infinite' : 'none',
      }}>
        {myTurn ? '👤' : <Avatar user={partner} size={28} profileImage={partner?.picture || null} />}
      </div>

      {/* Turn label */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: myTurn ? _T.accent : C.white, fontSize: 13, fontWeight: 700 }}>
          {myTurn ? (nl ? '🟢 Jouw beurt!' : '🟢 Your turn!') : `⏳ ${partnerName}…`}
        </div>
        {statusMsg && (
          <div style={{ color: C.silver, fontSize: 11, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {statusMsg}
          </div>
        )}
      </div>

      {/* Scores */}
      <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 9, color: C.silver, letterSpacing: 0.5 }}>{nl ? 'JIJ' : 'YOU'}</div>
          <div style={{ fontSize: 18, color: _T.accent, fontWeight: 700, lineHeight: 1 }}>
            {myScore ?? '–'}
          </div>
          {scoreLabel && <div style={{ fontSize: 9, color: C.silver }}>{scoreLabel}</div>}
        </div>
        <div style={{ width: 1, background: _T.cardBorderBot, alignSelf: 'stretch' }} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 9, color: C.silver, letterSpacing: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 50 }}>{(partnerName || '').toUpperCase().slice(0, 6)}</div>
          <div style={{ fontSize: 18, color: C.teal, fontWeight: 700, lineHeight: 1 }}>
            {partnerScore ?? '–'}
          </div>
          {scoreLabel && <div style={{ fontSize: 9, color: C.silver }}>{scoreLabel}</div>}
        </div>
      </div>

      <style>{`
        @keyframes turnPulse {
          0%,100% { box-shadow: 0 0 0 0 ${_T.accent}44; }
          50%      { box-shadow: 0 0 0 6px ${_T.accent}00; }
        }
      `}</style>
    </div>
  );
}

export default TurnBanner;
